"use client";

import { useEffect, useState, useCallback } from "react";

export interface SchedlyBiometric {
  isAvailable: () => Promise<boolean>;
  login: () => Promise<{ success: boolean; email?: string }>;
  enroll: (email: string, token: string) => Promise<void>;
  clearCredentials: () => Promise<void>;
}

export interface SchedlyNotifications {
  requestPermission: () => Promise<boolean>;
  getToken: () => Promise<{ token: string; platform: string; deviceId: string } | null>;
  setBadge: (count: number) => Promise<void>;
}

export interface SchedlyDeepLink {
  handle: (url: string) => void;
  getInitialLink: () => Promise<string | null>;
}

export interface PhotoResult {
  dataUrl: string;
  mimeType: string;
  format: string;
}

export interface SchedlyCamera {
  requestPermission: () => Promise<boolean>;
  takePhoto: () => Promise<PhotoResult | null>;
  pickFromGallery: () => Promise<PhotoResult | null>;
  pickWithPrompt: () => Promise<PhotoResult | null>;
  pickMultiple: (limit?: number) => Promise<PhotoResult[]>;
}

export interface ShareOptions {
  title?: string;
  text?: string;
  url?: string;
  dialogTitle?: string;
}

export interface SchedlyShare {
  canShare: () => Promise<boolean>;
  share: (options: ShareOptions) => Promise<boolean>;
  bookingLink: (url: string, clientName?: string) => Promise<boolean>;
  invoice: (url: string, invoiceNumber: string) => Promise<boolean>;
  receipt: (url: string) => Promise<boolean>;
}

export interface ContactResult {
  name: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

export interface SchedlyContacts {
  requestPermission: () => Promise<boolean>;
  pickContact: () => Promise<ContactResult | null>;
  getContacts: (query?: string) => Promise<ContactResult[]>;
}

export interface LocationResult {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: number;
}

export interface SchedlyLocation {
  requestPermission: () => Promise<boolean>;
  getCurrent: (highAccuracy?: boolean) => Promise<LocationResult | null>;
  watch: (watchId: string, callback: (loc: LocationResult) => void) => Promise<boolean>;
  clearWatch: (watchId: string) => Promise<void>;
  distanceKm: (lat1: number, lng1: number, lat2: number, lng2: number) => number;
  formatDistance: (km: number) => string;
}

export interface CachedBooking {
  id: string | number;
  title: string;
  startTime: string;
  endTime: string;
  attendeeName?: string;
  attendeeEmail?: string;
  status: string;
  location?: string;
}

export interface ScheduleCache {
  date: string;
  bookings: CachedBooking[];
  cachedAt: number;
}

export interface SchedlyOffline {
  isOffline: () => boolean;
  getSchedule: () => ScheduleCache | null;
  refresh: () => Promise<boolean>;
  clear: () => void;
  setupHandler: (onOffline: () => void, onOnline: () => void) => () => void;
}

export interface SchedlyReminders {
  requestPermission: () => Promise<boolean>;
  schedule: (opts: {
    bookingId: string;
    clientName: string;
    serviceName: string;
    startTime: Date;
    minutesBefore?: number;
  }) => Promise<number | null>;
  scheduleCheckIn: (opts: { bookingId: string; clientName: string; startTime: Date }) => Promise<number | null>;
  cancel: (id: number) => Promise<void>;
  cancelAll: () => Promise<void>;
  getPending: () => Promise<Array<{ id: number; title: string; at: Date | null }>>;
  onTapped: (callback: (bookingId: string) => void) => () => void;
}

export interface SchedlyClipboard {
  copy: (text: string) => Promise<boolean>;
  read: () => Promise<string | null>;
  copyBookingLink: (url: string) => Promise<boolean>;
  copyPhone: (phone: string) => Promise<boolean>;
  copyInvoiceLink: (url: string) => Promise<boolean>;
}

export interface SchedlyPayments {
  /** Terminal manager instance — call setProvider() then initialize() before collectPayment() */
  manager: {
    setProvider: (provider: string) => Promise<void>;
    initialize: (config: Record<string, string>) => Promise<void>;
    discoverReaders: () => Promise<unknown[]>;
    connectReader: (deviceId: string) => Promise<boolean>;
    disconnectReader: () => Promise<void>;
    collectPayment: (request: Record<string, unknown>) => Promise<Record<string, unknown>>;
    cancelPayment: () => Promise<void>;
    tapToPayAvailable: () => Promise<boolean>;
    getProvidersWithTapToPay: () => Promise<string[]>;
    getActiveDevice: () => unknown;
    getActiveProvider: () => string | null;
  };
  getProviders: () => string[];
}

export interface SchedlyNativeBridge {
  isNative: boolean;
  platform: "ios" | "android";
  version: string;
  biometric: SchedlyBiometric;
  notifications: SchedlyNotifications;
  deepLink: SchedlyDeepLink;
  camera: SchedlyCamera;
  reminders: SchedlyReminders;
  clipboard: SchedlyClipboard;
  share: SchedlyShare;
  contacts: SchedlyContacts;
  location: SchedlyLocation;
  offline: SchedlyOffline;
  payments: SchedlyPayments;
  haptic: (style?: "light" | "medium" | "heavy") => Promise<void>;
  clearBadge: () => Promise<void>;
  resetOnboarding: () => void;
}

declare global {
  interface Window {
    SchedlyNative?: SchedlyNativeBridge;
  }
}

export function getNativeBridge(): SchedlyNativeBridge | undefined {
  if (typeof window === "undefined") return undefined;
  return window.SchedlyNative;
}

export function useNativeApp() {
  const [isNative, setIsNative] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | null>(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return;

    setIsNative(true);
    setPlatform(bridge.platform);
    setIsOnline(!bridge.offline.isOffline());

    bridge.biometric
      .isAvailable()
      .then((available) => setBiometricAvailable(available))
      .catch(() => setBiometricAvailable(false));

    const cleanup = bridge.offline.setupHandler(
      () => setIsOnline(false),
      () => setIsOnline(true)
    );
    return cleanup;
  }, []);

  const takePhoto = useCallback(async (): Promise<PhotoResult | null> => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return null;
    return bridge.camera.pickWithPrompt();
  }, []);

  const shareBookingLink = useCallback(async (url: string, clientName?: string): Promise<boolean> => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return false;
    return bridge.share.bookingLink(url, clientName);
  }, []);

  const pickContact = useCallback(async (): Promise<ContactResult | null> => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return null;
    const granted = await bridge.contacts.requestPermission();
    if (!granted) return null;
    return bridge.contacts.pickContact();
  }, []);

  const getCurrentLocation = useCallback(async (): Promise<LocationResult | null> => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return null;
    const granted = await bridge.location.requestPermission();
    if (!granted) return null;
    return bridge.location.getCurrent();
  }, []);

  const getCachedSchedule = useCallback((): ScheduleCache | null => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return null;
    return bridge.offline.getSchedule();
  }, []);

  return {
    isNative,
    platform,
    biometricAvailable,
    isOnline,
    bridge: getNativeBridge(),
    takePhoto,
    shareBookingLink,
    pickContact,
    getCurrentLocation,
    getCachedSchedule,
  };
}
